import Title from "../components/title";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadedItemCard from "../components/MachshipTemplate/uploadedItemCard";
import { colors } from "../global";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import OverlayLoading from "../components/OverlayLoading";
import DropzoneComponent from "../components/Utils/DropzoneComponent";
import RenderInput from "../components/Utils/RenderInput";
import { apiFetch } from "../apiClient";

const MachshipTemplate = () => {
  const [files, setFiles] = useState([]);
  const [metadataMap, setMetadataMap] = useState({});
  const [accountDetails, setAccountDetails] = useState({
    customerName: "",
    machShipCompanyCode: "",
    spesificRequirements: "",
    accountCode: "",
    conPrefix: "",
    controlDocs: "",
    defaultMargin: "",
    margin: "",
    carrierPortal: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const informationInputField = [
    {
      key: "customerName",
      name: "Customer Name",
      type: "text",
    },
    {
      key: "machShipCompanyCode",
      name: "Machship Company Code",
      type: "text",
      tooltip:
        "Please provide the Machship Company Code also known as MSFC Code",
    },
    {
      key: "spesificRequirements",
      name: "Spesific Requirements",
      type: "text",
      tooltip: "Any Customer Specific Requirements or Restricitions",
    },
    {
      key: "accountCode",
      name: "Account code",
      type: "text",
    },
    {
      key: "conPrefix",
      name: "Con Prefix (If applicable)",
      type: "text",
    },
    {
      key: "controlDocs",
      name: "Control Docs Provided in the Email (Y/N)",
      type: "dropdown",
      options: [
        { value: "Y", name: "Yes" },
        { value: "N", name: "No" },
      ],
    },
    {
      key: "defaultMargin",
      name: "Default margin",
      type: "text",
    },
    {
      key: "margin",
      name: "Margin %",
      type: "text",
    },
    {
      key: "carrierPortal",
      name: "Carrier Portal Logins Received (Y/N)",
      type: "dropdown",
      options: [
        { value: "Y", name: "Yes" },
        { value: "N", name: "No" },
      ],
    },
  ];

  const changeValue = (key, value) => {
    setAccountDetails((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setIsSubmitting(true);
    const newFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const originalName = file.name;
        const fileExtension = originalName.split(".").pop();
        const fileName = originalName.replace(`.${fileExtension}`, "");

        const now = new Date();
        const timestamp = now
          .toISOString()
          .replace(/[-:]/g, "")
          .replace("T", "_")
          .split(".")[0];
        const newFileName = `${fileName}_${timestamp}.${fileExtension}`;
        let sheetList = [];
        if (fileExtension === "xlsx" || fileExtension === "xlsb") {
          try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: "array" });
            sheetList = workbook.SheetNames;
          } catch (error) {
            console.error("Error reading Excel file:", error);
          }
        }

        return {
          originalFile: file,
          renamedFile: new File([file], newFileName, { type: file.type }),
          sheetList,
        };
      })
    );

    setFiles((prev) => [...prev, ...newFiles]);
    setIsSubmitting(false);
  }, []);

  const handleMetadataUpdate = (filename, metadata) => {
    setMetadataMap((prev) => ({
      ...prev,
      [filename]: metadata,
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: true,
  });

  const handleSubmit = async () => {
    if (files.length === 0) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file.renamedFile));
      const metadataList = files.flatMap(
        (file) =>
          metadataMap[file.renamedFile.name]?.map((metadata) => ({
            file_name: file.renamedFile.name,
            ...metadata,
          })) || []
      );

      formData.append("metadata", JSON.stringify(metadataList));
      formData.append("accountDetails", JSON.stringify(accountDetails));
      const response = await apiFetch(`/api/v1/machshipTemplate/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }

        toast.error(errorMessage);
        return;
      }
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "_")
        .split(".")[0];

      const contentType = response.headers.get("Content-Type");

      if (
        response.ok &&
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Pricing_Template_${timestamp}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const errorText = await response.text();
        console.error("Download failed. Response not Excel:", errorText);
        toast.error(
          "Upload succeeded, but the server returned an unexpected format."
        );
      }
    } catch (err) {
      if (!err.message.includes("Upload failed.")) {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((file) => file.renamedFile.name !== name));
  };
  return (
    <>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}
      <div className="space-y-6 p-6 pb-4">
        <Title title={"Machship Converter"} />

        {/* Dropzone */}
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="CSV/XLSX"
        />

        {/* File List */}
        {files.length > 0 && (
          <>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3>Account Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {informationInputField.map((inputField, index) => (
                  <RenderInput
                    key={index}
                    input={inputField}
                    metadataMap={metadataMap}
                    changeValue={changeValue}
                    value={accountDetails[inputField.key]}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3
                  className="text-lg font-semibold"
                  style={{ color: colors.primary }}
                >
                  Files ({files.length})
                </h3>
                {files.length > 0 && (
                  <button
                    onClick={() => setFiles([])}
                    className="text-sm py-1 px-3 rounded hover:cursor-pointer"
                    style={{ color: colors.accent }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {files.map((file) => (
                  <UploadedItemCard
                    key={file.renamedFile.name}
                    file={file.renamedFile}
                    sheetList={file.sheetList}
                    onDelete={removeFile}
                    onUpdateMetadata={handleMetadataUpdate}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || files.length === 0}
                className="py-2 px-6 rounded-md font-medium shadow-sm transition-all flex items-center justify-center hover:cursor-pointer"
                style={{
                  backgroundColor: isSubmitting ? "#ccc" : colors.accent,
                  color: colors.white,
                  opacity: isSubmitting || files.length === 0 ? 0.7 : 1,
                }}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Convert Files"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MachshipTemplate;
